<script setup lang="ts">
import { ref, computed, onMounted, watch } from 'vue'
import { useAuthStore } from '../stores/auth'
import { useToastStore } from '../stores/toast'
import { useI18n } from 'vue-i18n'
import categoryService from '../services/category.service'
import type { Category } from '../types/category.types'

const authStore = useAuthStore()
const toastStore = useToastStore()
const { t } = useI18n()

// Check permissions
const hasPermission = (permission: string) => {
  const permissions = authStore.initData?.permission_model || []
  return permissions.includes(permission)
}

// Check if user can see any actions for category
const canSeeActions = computed(() => {
  return hasPermission('CATEGORY_SHOW') || hasPermission('CATEGORY_UPDATE') || hasPermission('CATEGORY_DELETE')
})

const categories = ref<Category[]>([])
const loading = ref(false)
const error = ref<string | null>(null)

// Pagination
const currentPage = ref(1)
const perPage = ref(25)
const totalCategories = ref(0)
const lastPage = ref(1)
const fromItem = ref(0)
const toItem = ref(0)

// Modal
const showDeleteModal = ref(false)
const categoryToDelete = ref<Category | null>(null)

// Dropdowns
const openDropdownId = ref<string | null>(null)

// Accordions (mobile)
const openAccordionIds = ref<Set<string>>(new Set())

// Get club UUID from init data
const clubUuid = computed(() => {
  return authStore.initData?.club?.uuid || null
})

const fetchCategories = async () => {
  // If no initData but user is authenticated, fetch it first
  if (!authStore.initData && authStore.user?.uuid) {
    try {
      await authStore.initUserData(authStore.user.uuid)
    } catch (err) {
      error.value = t('categories.errorNoInitData')
      console.error('Failed to load init data:', err)
      return
    }
  }

  if (!clubUuid.value) {
    error.value = `${t('categories.errorNoClub')}. ${t('categories.checkInitData')}`
    console.error('No clubUuid found in initData')
    return
  }

  loading.value = true
  error.value = null

  try {
    const response = await categoryService.getCategories(clubUuid.value, currentPage.value, perPage.value)

    // Update categories and pagination data
    categories.value = response.data
    totalCategories.value = response.total || 0
    lastPage.value = response.last_page || 1
    currentPage.value = response.current_page || 1
    fromItem.value = response.from || 0
    toItem.value = response.to || 0
  } catch (err: any) {
    error.value = err.response?.data?.message || t('categories.errorFetchingCategories')
    console.error('Fetch categories error:', err)
  } finally {
    loading.value = false
  }
}

const toggleDropdown = (categoryUuid: string) => {
  if (openDropdownId.value === categoryUuid) {
    openDropdownId.value = null
  } else {
    openDropdownId.value = categoryUuid
  }
}

const closeDropdowns = () => {
  openDropdownId.value = null
}

const toggleAccordion = (categoryUuid: string) => {
  if (openAccordionIds.value.has(categoryUuid)) {
    openAccordionIds.value.delete(categoryUuid)
  } else {
    openAccordionIds.value.add(categoryUuid)
  }
}

const openDeleteModal = (category: Category) => {
  categoryToDelete.value = category
  showDeleteModal.value = true
}

const closeDeleteModal = () => {
  categoryToDelete.value = null
  showDeleteModal.value = false
}

const confirmDelete = async () => {
  if (!categoryToDelete.value || !clubUuid.value) return

  try {
    const response = await categoryService.deleteCategory(clubUuid.value, categoryToDelete.value.uuid)

    // Show error toast with message from backend (red for deletion)
    const message = response?.message || t('categories.categoryDeleted')
    toastStore.error(message)

    // Refresh categories list
    await fetchCategories()
    closeDeleteModal()
  } catch (err: any) {
    error.value = err.response?.data?.message || t('categories.errorDeletingCategory')
    console.error('Delete category error:', err)
    closeDeleteModal()
  }
}

// Check if category is in last three (for dropdown positioning)
const isLastThree = (index: number) => {
  return index >= categories.value.length - 3
}

// Pagination methods
const changePage = (page: number) => {
  if (page < 1 || page > lastPage.value) return
  currentPage.value = page
  fetchCategories()
}

const nextPage = () => {
  if (currentPage.value < lastPage.value) {
    changePage(currentPage.value + 1)
  }
}

const prevPage = () => {
  if (currentPage.value > 1) {
    changePage(currentPage.value - 1)
  }
}

// Watch for perPage changes and reset to first page
watch(perPage, () => {
  currentPage.value = 1
  fetchCategories()
})

// Computed for visible page numbers
const visiblePages = computed(() => {
  const pages: number[] = []
  const maxVisible = 5
  let start = Math.max(1, currentPage.value - Math.floor(maxVisible / 2))
  let end = Math.min(lastPage.value, start + maxVisible - 1)

  if (end - start + 1 < maxVisible) {
    start = Math.max(1, end - maxVisible + 1)
  }

  for (let i = start; i <= end; i++) {
    pages.push(i)
  }

  return pages
})

onMounted(() => {
  fetchCategories()

  // Close dropdowns when clicking outside
  document.addEventListener('click', closeDropdowns)
})
</script>

<template>
  <main class="bg-gray-50 min-h-screen">
    <div class="w-full px-4 py-4 md:py-4 py-2">
      <!-- Page Header -->
      <div class="mb-4 md:mb-4 mb-2 flex justify-between items-center">
        <h1 class="text-2xl font-bold text-gray-900">
          {{ t('categories.title') }} <span class="text-lg font-normal text-gray-600 max-[499px]:block">({{ t('categories.found') }}: {{ totalCategories }})</span>
        </h1>
        <router-link
          v-if="hasPermission('CATEGORY_STORE')"
          to="/categories/create"
          class="px-4 py-2 md:px-4 md:py-2 px-2 py-1 bg-green-600 text-sm md:text-sm text-xs font-medium text-white rounded hover:bg-green-700 border-2 border-green-600 flex items-center gap-2 md:gap-2 gap-1"
        >
          <svg class="w-4 h-4 md:w-4 md:h-4 w-3 h-3" fill="currentColor" viewBox="0 0 20 20">
            <path fill-rule="evenodd" d="M10 3a1 1 0 011 1v5h5a1 1 0 110 2h-5v5a1 1 0 11-2 0v-5H4a1 1 0 110-2h5V4a1 1 0 011-1z" clip-rule="evenodd"/>
          </svg>
          <span class="md:inline hidden">{{ t('categories.addCategory') }}</span>
          <span class="md:hidden inline">{{ t('categories.addCategoryShort') }}</span>
        </router-link>
      </div>

      <!-- Error Message -->
      <div v-if="error" class="mb-4 p-4 bg-red-50 border-2 border-red-300 text-red-700 rounded">
        {{ error }}
      </div>

      <!-- Loading State -->
      <div v-if="loading" class="text-center py-8">
        <div class="inline-block animate-spin rounded-full h-8 w-8 border-b-2 border-gray-900"></div>
        <p class="mt-2 text-gray-600">{{ t('categories.loadingCategories') }}</p>
      </div>

      <!-- Desktop Table -->
      <div v-else class="hidden md:block bg-white border-2 border-gray-300 rounded shadow-sm">
        <div style="overflow-x: clip; overflow-y: visible;">
          <table class="w-full text-sm">
            <thead class="bg-gray-100 border-b-2 border-gray-300">
              <tr>
                <th class="px-4 py-3 text-left font-semibold text-gray-700">{{ t('categories.categoryName') }}</th>
                <th class="px-4 py-3 text-left font-semibold text-gray-700">{{ t('categories.playersCount') }}</th>
                <th class="px-4 py-3 text-left font-semibold text-gray-700">{{ t('categories.coaches') }}</th>
                <th v-if="canSeeActions" class="px-4 py-3 text-right font-semibold text-gray-700 sticky right-0 bg-gray-100"></th>
              </tr>
            </thead>
            <tbody class="divide-y divide-gray-200">
              <tr v-for="(category, index) in categories" :key="category.uuid" class="hover:bg-gray-50">
                <td class="px-4 py-3 text-gray-900 font-medium">
                  <router-link :to="`/categories/${category.uuid}`" class="text-blue-600 hover:underline">
                    {{ category.name }}
                  </router-link>
                </td>
                <td class="px-4 py-3 text-gray-700">{{ category.players_count || 0 }}</td>
                <td class="px-4 py-3 text-gray-700">
                  <template v-if="!category.coaches || category.coaches.length === 0">
                    <span class="text-gray-400">-</span>
                  </template>
                  <template v-else>
                    <template v-for="(coach, idx) in category.coaches" :key="coach.uuid">
                      <router-link :to="`/coaches/${coach.uuid}`" class="text-blue-600 hover:underline">
                        {{ coach.last_name }} {{ coach.first_name }}
                      </router-link>
                      <span v-if="idx < category.coaches.length - 1">, </span>
                    </template>
                  </template>
                </td>
                <td v-if="canSeeActions" class="px-4 py-3 sticky right-0 bg-inherit text-right" style="position: relative;">
                  <button
                    @click.stop="toggleDropdown(category.uuid)"
                    class="px-3 py-1.5 bg-gray-100 text-xs font-medium text-gray-700 rounded hover:bg-gray-200 border border-gray-300"
                  >
                    {{ t('common.actions') }}
                  </button>
                  <div
                    :class="[
                      'custom-dropdown',
                      'dropdown-bottom',
                      { 'show': openDropdownId === category.uuid }
                    ]"
                  >
                    <router-link v-if="hasPermission('CATEGORY_SHOW')" :to="`/categories/${category.uuid}`">{{ t('common.view') }}</router-link>
                    <router-link v-if="hasPermission('CATEGORY_UPDATE')" :to="`/categories/${category.uuid}/edit`">{{ t('common.edit') }}</router-link>
                    <button v-if="hasPermission('CATEGORY_DELETE')" class="delete-action" @click="openDeleteModal(category)">{{ t('common.delete') }}</button>
                  </div>
                </td>
              </tr>
            </tbody>
          </table>
        </div>
      </div>

      <!-- Mobile Accordion -->
      <div v-if="!loading" class="md:hidden space-y-2">
        <div v-for="category in categories" :key="category.uuid" class="bg-white border-2 border-gray-300 rounded shadow-sm">
          <!-- Accordion Header -->
          <div class="flex items-center justify-between p-3 cursor-pointer" @click="toggleAccordion(category.uuid)">
            <div class="flex-1 min-w-0">
              <router-link
                :to="`/categories/${category.uuid}`"
                class="text-sm font-medium text-blue-600 hover:underline"
                @click.stop
              >
                {{ category.name }}
              </router-link>
              <div class="text-xs text-gray-600 mt-1">{{ t('categories.players') }}: {{ category.players_count || 0 }}</div>
            </div>
            <div class="flex items-center gap-2 ml-2">
              <div v-if="canSeeActions" style="position: relative;">
                <button
                  @click.stop="toggleDropdown(category.uuid)"
                  class="px-3 py-1.5 bg-gray-100 text-xs font-medium text-gray-700 rounded hover:bg-gray-200 border border-gray-300"
                >
                  {{ t('common.actions') }}
                </button>
                <div
                  :class="['custom-dropdown', 'dropdown-bottom', { 'show': openDropdownId === category.uuid }]"
                >
                  <router-link v-if="hasPermission('CATEGORY_SHOW')" :to="`/categories/${category.uuid}`">{{ t('common.view') }}</router-link>
                  <router-link v-if="hasPermission('CATEGORY_UPDATE')" :to="`/categories/${category.uuid}/edit`">{{ t('common.edit') }}</router-link>
                  <button v-if="hasPermission('CATEGORY_DELETE')" class="delete-action" @click="openDeleteModal(category)">{{ t('common.delete') }}</button>
                </div>
              </div>
              <svg
                :class="['w-5 h-5 text-gray-600 transition-transform', { 'rotate-180': openAccordionIds.has(category.uuid) }]"
                fill="currentColor"
                viewBox="0 0 20 20"
              >
                <path fill-rule="evenodd" d="M5.293 7.293a1 1 0 011.414 0L10 10.586l3.293-3.293a1 1 0 111.414 1.414l-4 4a1 1 0 01-1.414 0l-4-4a1 1 0 010-1.414z" clip-rule="evenodd"/>
              </svg>
            </div>
          </div>
          <!-- Accordion Content -->
          <div v-show="openAccordionIds.has(category.uuid)" class="border-t-2 border-gray-300 p-3">
            <div class="space-y-2 text-xs">
              <div>
                <span class="font-semibold text-gray-700">{{ t('categories.coaches') }}:</span>
                <span class="text-gray-600">
                  <template v-if="!category.coaches || category.coaches.length === 0">
                    -
                  </template>
                  <template v-else>
                    <template v-for="(coach, idx) in category.coaches" :key="coach.uuid">
                      <router-link :to="`/coaches/${coach.uuid}`" class="text-blue-600 hover:underline">
                        {{ coach.last_name }} {{ coach.first_name }}
                      </router-link>
                      <span v-if="idx < category.coaches.length - 1">, </span>
                    </template>
                  </template>
                </span>
              </div>
            </div>
          </div>
        </div>
      </div>

      <!-- Pagination -->
      <div v-if="!loading && totalCategories > 0" class="mt-4 flex flex-col lg:flex-row justify-between items-center gap-4">
        <div class="text-sm text-gray-700 w-full lg:w-auto text-center lg:text-left">
          {{ t('categories.displaying') }} {{ fromItem }} - {{ toItem }} {{ t('categories.of') }} {{ totalCategories }} {{ t('categories.categoriesWord') }}
        </div>
        <div class="flex flex-col md:flex-row items-center gap-4 w-full lg:w-auto">
          <div class="flex items-center gap-2 w-full md:w-auto justify-center md:justify-start">
            <label for="perPage" class="text-sm text-gray-700">{{ t('categories.perPage') }}</label>
            <select id="perPage" v-model.number="perPage" class="px-3 py-1 border-2 border-gray-300 rounded text-sm bg-white">
              <option :value="25">25</option>
              <option :value="50">50</option>
              <option :value="75">75</option>
              <option :value="100">100</option>
            </select>
          </div>
          <div class="flex items-center gap-1 w-full md:w-auto justify-center md:justify-start">
            <button
              @click="prevPage"
              :disabled="currentPage === 1"
              class="px-3 py-1 border-2 border-gray-300 rounded text-sm bg-white hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
            >
              &laquo;
            </button>
            <button
              v-for="page in visiblePages"
              :key="page"
              @click="changePage(page)"
              :class="[
                'px-3 py-1 border-2 rounded text-sm',
                page === currentPage
                  ? 'border-gray-900 bg-gray-900 text-white font-medium'
                  : 'border-gray-300 bg-white hover:bg-gray-50'
              ]"
            >
              {{ page }}
            </button>
            <button
              @click="nextPage"
              :disabled="currentPage === lastPage"
              class="px-3 py-1 border-2 border-gray-300 rounded text-sm bg-white hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
            >
              &raquo;
            </button>
          </div>
        </div>
      </div>
    </div>
  </main>

  <!-- Delete Confirmation Modal -->
  <div
    v-if="showDeleteModal"
    class="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4"
    @click.self="closeDeleteModal"
  >
    <div class="bg-white rounded-lg shadow-xl max-w-md w-full">
      <div class="p-6">
        <h2 class="text-xl font-bold text-gray-900 mb-4">{{ t('categories.deleteConfirmation') }}</h2>
        <p class="text-gray-700 mb-6">
          {{ t('categories.deleteConfirmationMessage') }} "<span class="font-semibold">{{ categoryToDelete?.name }}</span>"?
        </p>

        <div class="flex gap-3 justify-end">
          <button
            @click="closeDeleteModal"
            class="px-4 py-2 bg-gray-100 text-sm font-medium text-gray-700 rounded hover:bg-gray-200 border-2 border-gray-300"
          >
            {{ t('common.cancel') }}
          </button>
          <button
            @click="confirmDelete"
            class="px-4 py-2 bg-red-600 text-sm font-medium text-white rounded hover:bg-red-700"
          >
            {{ t('common.delete') }}
          </button>
        </div>
      </div>
    </div>
  </div>
</template>

<style scoped>
</style>
